/**
 * RequestsTab - Three-column layout for request triage
 * Left: Inbox (email-style list)
 * Center: Consideration Board (Kanban)
 * Right: Collection Co-pilot (drawer)
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { requestsApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useTimezone } from '../../hooks/useTimezone';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  ArrowRight,
  Merge,
  Trash2,
  Inbox,
  Target,
  Users,
  Flag,
  Rocket,
  Layers,
  GripVertical,
  Sparkles,
  Bug,
  Loader2,
  MessageSquare,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import RequestEditModal from './RequestEditModal';
import RequestRejectModal from './RequestRejectModal';
import CollectionCopilotDrawer from '../copilot/CollectionCopilotDrawer';
import { useEntityModal } from '../../contexts/EntityModalContext';
import { toast } from 'sonner';

// Type colors and icons - Order: Bug, Feature, Epic, Initiative, Goal, Persona
const TYPE_CONFIG = {
  bug: { color: 'bg-red-500', icon: Bug, label: 'Bug' },
  feature: { color: 'bg-blue-500', icon: Sparkles, label: 'Feature' },
  epic: { color: 'bg-purple-500', icon: Layers, label: 'Epic' },
  initiative: { color: 'bg-orange-500', icon: Rocket, label: 'Initiative' },
  goal: { color: 'bg-yellow-500', icon: Target, label: 'Goal' },
  persona: { color: 'bg-green-500', icon: Users, label: 'Persona' },
};

/**
 * RequestEntityModals - Renders request modals triggered from entity links in chat
 */
const RequestEntityModals = ({ productId, onRefresh, markStale }) => {
  const { selectedEntity, closeEntityModal } = useEntityModal();
  
  if (!selectedEntity || selectedEntity.type !== 'request') return null;
  
  return (
    <RequestEditModal
      isOpen={true}
      onClose={closeEntityModal}
      request={selectedEntity.data}
      productId={productId}
      onSave={() => {
        closeEntityModal();
        onRefresh?.();
      }}
      markStale={markStale}
    />
  );
};

// Consideration board columns
const CONSIDERATION_COLUMNS = [
  { id: 'needs_definition', label: 'Needs Definition' },
  { id: 'ready_to_rank', label: 'Ready to Rank', showRankedBadge: true },
  { id: 'ready_to_commit', label: 'Ready to Commit' },
];

const RequestsTab = ({ product, canEdit, markStale }) => {
  const { getThemeColors } = useSafeMode();
  const { formatDateSmart } = useTimezone();
  const colors = getThemeColors();
  
  // Data state
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ inbox_count: 0, considering_count: 0 });
  
  // UI state
  const [inboxCollapsed, setInboxCollapsed] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterType, setFilterType] = useState('all');
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  
  // Rejection modal state
  const [rejectingRequest, setRejectingRequest] = useState(null);
  
  // Drag and drop state (matching Releases pattern)
  const [draggedRequest, setDraggedRequest] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  
  // Optimistic UI state
  const [pendingMoves, setPendingMoves] = useState(new Set());
  const [optimisticOverrides, setOptimisticOverrides] = useState({});
  
  // Check if a request is currently being moved
  const isRequestPending = (requestId) => pendingMoves.has(requestId);
  
  // Apply optimistic overrides to requests for rendering
  const effectiveRequests = useMemo(() => {
    return requests.map(r => {
      if (optimisticOverrides[r.request_id] !== undefined) {
        return { ...r, ...optimisticOverrides[r.request_id] };
      }
      return r;
    });
  }, [requests, optimisticOverrides]);
  
  // Deep link handling - check URL params for request to open
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const openType = urlParams.get('open');
    const entityId = urlParams.get('id');
    
    if (openType === 'request' && entityId && requests.length > 0 && !editingRequest) {
      const targetRequest = requests.find(r => r.request_id === entityId);
      if (targetRequest) {
        setEditingRequest(targetRequest);
        // Clear URL params after handling
        const newParams = new URLSearchParams(urlParams);
        newParams.delete('open');
        newParams.delete('id');
        window.history.replaceState({}, '', `${window.location.pathname}?${newParams.toString()}`);
      }
    }
  }, [requests, editingRequest]);
  
  // Load requests
  const loadRequests = useCallback(async () => {
    if (!product?.product_id) return;
    
    try {
      setLoading(true);
      const [requestsRes, statsRes] = await Promise.all([
        requestsApi.list(product.product_id),
        requestsApi.getStats(product.product_id),
      ]);
      setRequests(requestsRes.data || []);
      setStats(statsRes.data || { inbox_count: 0, considering_count: 0 });
    } catch (err) {
      console.error('Error loading requests:', err);
    } finally {
      setLoading(false);
    }
  }, [product?.product_id]);
  
  useEffect(() => {
    loadRequests();
  }, [loadRequests]);
  
  // Filter requests by stage - use effectiveRequests for optimistic UI
  const inboxRequests = effectiveRequests.filter(r => r.stage === 'new' && !r.merged_into);
  const consideringRequests = effectiveRequests.filter(r => r.stage === 'considering' && !r.merged_into);
  
  // Build injectedContext with requests data for AI to analyze
  const injectedContext = useMemo(() => {
    // Filter out rejected requests, include all other statuses
    const activeRequests = requests.filter(r => r.stage !== 'rejected' && !r.merged_into);
    if (activeRequests.length === 0) return null;
    
    // Format requests as structured JSON for AI
    const formattedRequests = activeRequests.map(r => ({
      id: r.request_id,
      title: r.title,
      description: r.description || '',
      type: r.type,
      priority: r.priority || 'Medium',
      source: r.source || 'internal',
      stage: r.stage,
      created_at: r.created_at,
    }));
    
    return `=== PRODUCT REQUESTS ===
Total Active Requests: ${activeRequests.length}
Inbox (New): ${inboxRequests.length}
Considering: ${consideringRequests.length}

${JSON.stringify(formattedRequests, null, 2)}
=== END PRODUCT REQUESTS ===`;
  }, [requests, inboxRequests.length, consideringRequests.length]);
  
  // Apply search and filters to inbox
  const filteredInboxRequests = inboxRequests
    .filter(r => {
      if (filterType !== 'all' && r.type !== filterType) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return r.title.toLowerCase().includes(query) || 
               r.description?.toLowerCase().includes(query);
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      if (sortBy === 'type') return a.type.localeCompare(b.type);
      return 0;
    });
  
  // Toggle selection
  const toggleSelect = (requestId) => {
    setSelectedRequests(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };
  
  const selectAll = () => {
    if (selectedRequests.length === filteredInboxRequests.length) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(filteredInboxRequests.map(r => r.request_id));
    }
  };
  
  // Bulk actions
  const handleBulkMove = async (stage, status = null) => {
    if (selectedRequests.length === 0) return;
    
    try {
      await requestsApi.bulkUpdate(selectedRequests, stage, status);
      setSelectedRequests([]);
      loadRequests();
    } catch (err) {
      console.error('Error moving requests:', err);
    }
  };
  
  const handleBulkReject = async () => {
    if (selectedRequests.length === 0) return;
    
    // For bulk reject, we'll use a generic reason
    // TODO: Could enhance to show modal for bulk rejection reason
    try {
      await requestsApi.bulkUpdate(selectedRequests, 'rejected');
      setSelectedRequests([]);
      loadRequests();
    } catch (err) {
      console.error('Error rejecting requests:', err);
    }
  };
  
  // Handle single request rejection with reason
  const handleRejectRequest = (request) => {
    setRejectingRequest(request);
  };
  
  const handleConfirmReject = async (rejectionData) => {
    if (!rejectingRequest) return;
    
    try {
      await requestsApi.update(rejectingRequest.request_id, { 
        stage: 'rejected',
        rejection_category: rejectionData.rejection_category,
        rejection_reason: rejectionData.rejection_reason
      });
      toast.success('Request rejected');
      loadRequests();
    } catch (err) {
      console.error('Error rejecting request:', err);
      toast.error('Failed to reject request');
    } finally {
      setRejectingRequest(null);
    }
  };
  
  const handleMerge = async () => {
    if (selectedRequests.length < 2) return;
    
    // Merge all selected into the first one
    const [target, ...sources] = selectedRequests;
    try {
      await requestsApi.merge(sources, target);
      setSelectedRequests([]);
      loadRequests();
    } catch (err) {
      console.error('Error merging requests:', err);
    }
  };
  
  // Single item actions
  const handleUpdateRequest = async (requestId, updates) => {
    try {
      await requestsApi.update(requestId, updates);
      loadRequests();
    } catch (err) {
      console.error('Error updating request:', err);
      toast.error('Failed to update request');
    }
  };
  
  // Drag and drop for kanban (aligned with Releases pattern)
  const handleDragStart = (e, request) => {
    // Don't allow dragging requests that are pending
    if (isRequestPending(request.request_id)) {
      e.preventDefault();
      return;
    }
    setDraggedRequest(request);
    e.dataTransfer.setData('request_id', request.request_id);
    e.dataTransfer.setData('from_stage', request.stage);
    e.dataTransfer.setData('from_status', request.consideration_status || '');
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (columnId !== undefined) {
      setDragOverColumn(columnId);
    }
  };
  
  const handleDragLeave = () => {
    setDragOverColumn(null);
  };
  
  // Drop on consideration board column
  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedRequest) return;
    
    const requestId = draggedRequest.request_id;
    const fromStatus = draggedRequest.consideration_status;
    
    // Don't do anything if dropping in same location
    if (draggedRequest.stage === 'considering' && fromStatus === newStatus) {
      setDraggedRequest(null);
      return;
    }
    
    // STEP 1: Optimistic UI - immediately show card in target location
    setPendingMoves(prev => new Set([...prev, requestId]));
    setOptimisticOverrides(prev => ({ 
      ...prev, 
      [requestId]: { stage: 'considering', consideration_status: newStatus } 
    }));
    setDraggedRequest(null);
    
    // STEP 2: Make API call in background
    try {
      await requestsApi.update(requestId, { 
        stage: 'considering', 
        consideration_status: newStatus 
      });
      
      // STEP 3: Success - refresh data and clear optimistic state
      await loadRequests();
      
      setOptimisticOverrides(prev => {
        const updated = { ...prev };
        delete updated[requestId];
        return updated;
      });
    } catch (err) {
      console.error('Error moving request:', err);
      
      // STEP 4: Failure - revert optimistic update
      setOptimisticOverrides(prev => {
        const updated = { ...prev };
        delete updated[requestId];
        return updated;
      });
      
      toast.error('Failed to move request. Please try again.');
    } finally {
      setPendingMoves(prev => {
        const updated = new Set(prev);
        updated.delete(requestId);
        return updated;
      });
    }
  };
  
  // Drop on inbox (move back from consideration board)
  const handleDropToInbox = async (e) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedRequest) return;
    
    const requestId = draggedRequest.request_id;
    
    // Only allow moving from considering back to inbox
    if (draggedRequest.stage !== 'considering') {
      setDraggedRequest(null);
      return;
    }
    
    // STEP 1: Optimistic UI
    setPendingMoves(prev => new Set([...prev, requestId]));
    setOptimisticOverrides(prev => ({ 
      ...prev, 
      [requestId]: { stage: 'new', consideration_status: null } 
    }));
    setDraggedRequest(null);
    
    // STEP 2: Make API call
    try {
      await requestsApi.update(requestId, { 
        stage: 'new', 
        consideration_status: null 
      });
      
      await loadRequests();
      
      setOptimisticOverrides(prev => {
        const updated = { ...prev };
        delete updated[requestId];
        return updated;
      });
    } catch (err) {
      console.error('Error moving request to inbox:', err);
      
      setOptimisticOverrides(prev => {
        const updated = { ...prev };
        delete updated[requestId];
        return updated;
      });
      
      toast.error('Failed to move request. Please try again.');
    } finally {
      setPendingMoves(prev => {
        const updated = new Set(prev);
        updated.delete(requestId);
        return updated;
      });
    }
  };
  
  // Format date using user's timezone
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Handle future dates or just created (within last minute)
    if (diffMs < 60000) return 'Just now';
    if (diffDays < 0) return 'Just now'; // Handle timezone differences
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDateSmart(dateStr);
  };

  return (
    <>
    {loading ? (
    <div className="flex items-center justify-center h-full">
      <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
    </div>
    ) : requests.length === 0 ? (
    <div className="flex flex-row h-full overflow-hidden">
      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-6xl mx-auto">
          <div className={cn("text-center py-12 px-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)} data-testid="requests-empty-state">
            <Inbox className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
            <h3 className={cn("text-lg font-semibold mb-4", colors.textPrimary)}>No requests yet</h3>
            <div className={cn("text-sm mb-6 max-w-2xl mx-auto text-left space-y-4", colors.textMuted)}>
              <p>
                <strong className={colors.textPrimary}>Centralize the Intake Stream</strong>
                <br />
                Product managers often face a fragmented influx of ideas from customer interviews, stakeholder meetings, and market shifts. In StackRanked, the Requests tab acts as a unified clearinghouse for this raw data. Whether an entry is manually recorded during a call or automatically generated by your AI Co-Pilot through the Ideation tools - including Collaboration spaces, Feedback Forms, and Market Reports - it lands here for systematic evaluation. By centralizing these inputs, you ensure that no critical insight is lost in spreadsheets or chat threads.
              </p>
              <p>
                <strong className={colors.textPrimary}>The Role of Requests in the StackRanked Process</strong>
                <br />
                This tool transforms abstract suggestions into actionable product intelligence. The Requests board follows a structured lifecycle - Needs Definition, Ready to Rank, and Ready to Commit - designed to move an idea from raw concept to strategic entity. Your AI assistants use the context within these requests to stress-test their alignment with your established Strategy and Personas. This process allows you to determine the objective value of a request before it ever consumes engineering resources.
              </p>
              <p>
                <strong className={colors.textPrimary}>Moving from Consideration to Commitment</strong>
                <br />
                StackRanked provides a flexible path for validation. You can apply manual oversight or utilize the Stack Ranking tool to let customers and stakeholders weigh in directly on what matters most. Once a request reaches &ldquo;Ready to Commit,&rdquo; you can convert it into a formal entity - such as a Feature, Epic, Initiative, or Goal. This transition ensures that every item on your roadmap has been rigorously vetted, ranked by importance, and carries the full context of its original discovery.
              </p>
            </div>
            {canEdit && (
              <Button className={cn(colors.accentGradient)} onClick={() => setShowCreateModal(true)} data-testid="create-first-request-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Request
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    ) : (
    <div className="flex flex-row h-full overflow-hidden">
      {/* Left: Inbox */}
      <div 
        className={cn(
          "flex flex-col border-r transition-all duration-300",
          colors.border,
          inboxCollapsed ? "w-12" : "w-[30%] min-w-[300px]"
        )}
      >
        {inboxCollapsed ? (
          // Collapsed state
          <div className="flex flex-col items-center py-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setInboxCollapsed(false)}
              className="mb-2"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="rotate-90 whitespace-nowrap flex items-center gap-2 text-sm font-medium mt-4">
              <Inbox className="h-4 w-4 rotate-[-90deg]" />
              <span>Inbox ({stats.inbox_count})</span>
            </div>
          </div>
        ) : (
          <>
            {/* Inbox Header */}
            <div className={cn("p-3 border-b", colors.border)}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Inbox className="h-4 w-4" />
                  <span className="font-semibold">Inbox</span>
                  <Badge variant="secondary" className="text-xs">
                    {stats.inbox_count}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setInboxCollapsed(true)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Search and Sort */}
              <div className="flex gap-2 mb-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-24 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="oldest">Oldest</SelectItem>
                    <SelectItem value="type">Type</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Type Filter */}
              <div className="flex gap-1 flex-wrap">
                <Badge 
                  variant={filterType === 'all' ? 'default' : 'outline'}
                  className="cursor-pointer text-xs"
                  onClick={() => setFilterType('all')}
                >
                  All
                </Badge>
                {Object.entries(TYPE_CONFIG).map(([type, config]) => (
                  <Badge
                    key={type}
                    variant={filterType === type ? 'default' : 'outline'}
                    className="cursor-pointer text-xs"
                    onClick={() => setFilterType(type)}
                  >
                    {config.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            {/* Bulk Actions Bar */}
            {selectedRequests.length > 0 && (
              <div className={cn("p-2 border-b flex items-center gap-2 text-sm", colors.border, colors.cardBg)}>
                <span className="text-muted-foreground">{selectedRequests.length} selected</span>
                <div className="flex-1" />
                <Button size="sm" variant="outline" onClick={() => handleBulkMove('considering', 'needs_definition')}>
                  <ArrowRight className="h-3 w-3 mr-1" /> Move
                </Button>
                {selectedRequests.length >= 2 && (
                  <Button size="sm" variant="outline" onClick={handleMerge}>
                    <Merge className="h-3 w-3 mr-1" /> Merge
                  </Button>
                )}
                <Button size="sm" variant="outline" className="text-destructive" onClick={handleBulkReject}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            )}
            
            {/* Request List */}
            <div className="flex-1 overflow-y-auto">
              {/* Select All Row */}
              {filteredInboxRequests.length > 0 && (
                <div 
                  className={cn(
                    "px-3 py-2 border-b flex items-center gap-2 text-xs text-muted-foreground",
                    colors.border
                  )}
                >
                  <Checkbox
                    checked={selectedRequests.length === filteredInboxRequests.length && filteredInboxRequests.length > 0}
                    onCheckedChange={selectAll}
                  />
                  <span>Select all</span>
                </div>
              )}
              
              {filteredInboxRequests.length === 0 ? (
                <div 
                  className={cn(
                    "flex flex-col items-center justify-center py-12 text-muted-foreground border-2 border-dashed rounded-lg m-2 transition-all",
                    dragOverColumn === 'inbox' && "ring-2 ring-blue-500 border-blue-500"
                  )}
                  onDrop={handleDropToInbox}
                  onDragOver={(e) => handleDragOver(e, 'inbox')}
                  onDragLeave={handleDragLeave}
                >
                  <Inbox className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">No requests in inbox</p>
                  <p className="text-xs mt-1">Drop requests here to move back</p>
                </div>
              ) : (
                <div 
                  className={cn(
                    "transition-all rounded-lg",
                    dragOverColumn === 'inbox' && "ring-2 ring-blue-500"
                  )}
                  onDrop={handleDropToInbox}
                  onDragOver={(e) => handleDragOver(e, 'inbox')}
                  onDragLeave={handleDragLeave}
                >
                  {filteredInboxRequests.map(request => {
                    const typeConfig = TYPE_CONFIG[request.type] || TYPE_CONFIG.feature;
                    const TypeIcon = typeConfig.icon;
                    const isPending = isRequestPending(request.request_id);
                    
                    return (
                      <div
                        key={request.request_id}
                        draggable={!isPending}
                        onDragStart={(e) => handleDragStart(e, request)}
                        className={cn(
                          "px-3 py-2 border-b hover:bg-accent/50 transition-all",
                          colors.border,
                          selectedRequests.includes(request.request_id) && "bg-accent",
                          !isPending && "cursor-move",
                          draggedRequest?.request_id === request.request_id && "opacity-50",
                          isPending && "opacity-70 animate-pulse"
                        )}
                        onClick={() => !isPending && setEditingRequest(request)}
                      >
                        <div className="flex items-start gap-2">
                          {isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin text-blue-500 mt-1 flex-shrink-0" />
                          ) : (
                            <GripVertical className="h-4 w-4 text-muted-foreground mt-1 flex-shrink-0 opacity-50" />
                          )}
                          <Checkbox
                            checked={selectedRequests.includes(request.request_id)}
                            onCheckedChange={() => toggleSelect(request.request_id)}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1"
                            disabled={isPending}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-sm truncate">{request.title}</span>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDate(request.created_at)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <div className={cn("w-2 h-2 rounded-full", typeConfig.color)} />
                              <span className="text-xs text-muted-foreground">{typeConfig.label}</span>
                              {request.source?.submitted_by_name && (
                                <>
                                  <span className="text-xs text-muted-foreground">·</span>
                                  <span className="text-xs text-muted-foreground truncate">
                                  {request.source.submitted_by_name}
                                </span>
                              </>
                            )}
                            {request.source?.request_count > 1 && (
                              <Badge variant="secondary" className="text-xs h-4 px-1">
                                x{request.source.request_count}
                              </Badge>
                            )}
                          </div>
                          {/* Tags */}
                          {request.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {request.tags.slice(0, 3).map((tag, idx) => (
                                <span 
                                  key={idx}
                                  className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                >
                                  {tag}
                                </span>
                              ))}
                              {request.tags.length > 3 && (
                                <span className="text-xs text-muted-foreground">+{request.tags.length - 3}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <MoreVertical className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleUpdateRequest(request.request_id, { stage: 'considering', consideration_status: 'needs_definition' })}>
                              <ArrowRight className="h-3 w-3 mr-2" /> Move to Consider
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setEditingRequest(request)}>
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleRejectRequest(request)}
                            >
                              <Trash2 className="h-3 w-3 mr-2" /> Reject
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Center: Consideration Board */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Board Header */}
        <div className={cn("p-4 border-b flex items-center justify-between", colors.border)}>
          <div className="flex items-center gap-2">
            <Flag className="h-4 w-4" />
            <span className="font-semibold">Consideration Board</span>
            <Badge variant="secondary" className="text-xs">
              {stats.considering_count}
            </Badge>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" asChild data-testid="feedback-forms-btn">
                <Link to={`/products/${product?.product_id}?tab=ideation&space=support-forms`}>
                  <MessageSquare className="h-4 w-4 mr-1" /> Feedback Forms
                </Link>
              </Button>
              <Button size="sm" onClick={() => setShowCreateModal(true)} data-testid="add-request-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Request
              </Button>
            </div>
          )}
        </div>
        
        {/* Kanban Columns */}
        <div className="flex-1 flex gap-4 p-4 overflow-x-auto">
          {CONSIDERATION_COLUMNS.map(column => {
            let columnRequests = consideringRequests.filter(
              r => r.consideration_status === column.id
            );
            
            // Sort "Ready to Rank" column by rank_order if ranked
            if (column.id === 'ready_to_rank') {
              columnRequests = [...columnRequests].sort((a, b) => {
                // Ranked items come first, sorted by rank_order ascending
                const aRank = a.rank_order ?? Infinity;
                const bRank = b.rank_order ?? Infinity;
                if (aRank !== bRank) return aRank - bRank;
                // Fallback to creation date for unranked items
                return new Date(b.created_at) - new Date(a.created_at);
              });
            }
            
            // Check if column has been ranked (any item has stack_rank_id)
            const isRanked = column.showRankedBadge && columnRequests.some(r => r.stack_rank_id);
            
            return (
              <div
                key={column.id}
                className={cn(
                  "flex-1 min-w-[250px] flex flex-col rounded-lg border transition-all",
                  colors.cardBg,
                  colors.border,
                  dragOverColumn === column.id && "ring-2 ring-blue-500"
                )}
                onDrop={(e) => handleDrop(e, column.id)}
                onDragOver={(e) => handleDragOver(e, column.id)}
                onDragLeave={handleDragLeave}
              >
                {/* Column Header */}
                <div className={cn("p-3 border-b", colors.border)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{column.label}</span>
                      {column.showRankedBadge && isRanked && (
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                          Ranked
                        </Badge>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {columnRequests.length}
                    </Badge>
                  </div>
                </div>
                
                {/* Column Content */}
                <div className="flex-1 p-2 overflow-y-auto space-y-2">
                  {columnRequests.length === 0 ? (
                    <div className={cn(
                      "flex items-center justify-center h-24 text-sm text-muted-foreground border-2 border-dashed rounded-lg transition-all",
                      dragOverColumn === column.id && "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                    )}>
                      Drop requests here
                    </div>
                  ) : (
                    columnRequests.map((request, idx) => {
                      const typeConfig = TYPE_CONFIG[request.type] || TYPE_CONFIG.feature;
                      const showRankBadge = column.id === 'ready_to_rank' && request.rank_order;
                      const isPending = isRequestPending(request.request_id);
                      
                      return (
                        <div
                          key={request.request_id}
                          draggable={!isPending}
                          onDragStart={(e) => handleDragStart(e, request)}
                          className={cn(
                            "p-3 rounded-lg border hover:shadow-md transition-all group",
                            colors.border,
                            colors.background,
                            showRankBadge && "border-l-4 border-l-green-500",
                            !isPending && "cursor-move",
                            draggedRequest?.request_id === request.request_id && "opacity-50",
                            isPending && "opacity-70 animate-pulse"
                          )}
                          onClick={() => !isPending && setEditingRequest(request)}
                        >
                          <div className="flex items-start gap-2">
                            {isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin text-blue-500 mt-0.5 flex-shrink-0" />
                            ) : showRankBadge ? (
                              <div className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                {request.rank_order}
                              </div>
                            ) : (
                              <GripVertical className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2">
                                  <div className={cn("w-2 h-2 rounded-full flex-shrink-0", typeConfig.color)} />
                                  <span className="text-xs text-muted-foreground">{typeConfig.label}</span>
                                </div>
                                {/* Delete button - visible on hover */}
                                {!isPending && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRejectRequest(request);
                                  }}
                                >
                                  <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                                </Button>
                                )}
                              </div>
                              <p className="text-sm font-medium truncate">{request.title}</p>
                              {column.id === 'ready_to_commit' && !isPending && (
                                <Button
                                  size="sm"
                                  className="mt-2 w-full"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingRequest({ ...request, isCommitting: true });
                                  }}
                                >
                                  Commit →
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Right: Co-pilot Panel */}
      <CollectionCopilotDrawer
        context="requests"
        contextId={product?.product_id}
        title="Request Advisor"
        onRefresh={loadRequests}
        contextData={{ requests: requests.filter(r => !r.merged_into && r.stage !== 'rejected') }}
        injectedContext={injectedContext}
      />
      {/* Entity modals for request linking from chat */}
      <RequestEntityModals 
        productId={product?.product_id}
        onRefresh={loadRequests}
        markStale={markStale}
      />
      
      {/* Rejection Modal */}
      <RequestRejectModal
        open={!!rejectingRequest}
        onClose={() => setRejectingRequest(null)}
        onConfirm={handleConfirmReject}
        requestTitle={rejectingRequest?.title}
      />
    </div>
    )}

    {/* Modals always rendered for empty state create flow */}
    {(showCreateModal || editingRequest) && (
      <RequestEditModal
        request={editingRequest}
        productId={product?.product_id}
        isOpen={true}
        onClose={() => {
          setShowCreateModal(false);
          setEditingRequest(null);
        }}
        onSave={() => {
          setShowCreateModal(false);
          setEditingRequest(null);
          loadRequests();
        }}
        onRejectRequest={(request) => {
          setEditingRequest(null);
          handleRejectRequest(request);
        }}
        markStale={markStale}
        isCommitting={editingRequest?.isCommitting}
      />
    )}
    </>
  );
};

export default RequestsTab;
